import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { init as initAptabase, trackEvent } from "@aptabase/web";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FullScreenLoader } from "@/components/Loader";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import SeeItInAction from "@/pages/SeeItInAction";
import UseWithClaude from "@/pages/UseWithClaude";
import ConnectClaude from "@/pages/ConnectClaude";
import Authorize from "@/pages/Authorize";
import SkillBuilder from "@/pages/SkillBuilder";
import MySkills from "@/pages/MySkills";
import Upload from "@/pages/Upload";
import Marketplace from "@/pages/Marketplace";
import SkillDetail from "@/pages/SkillDetail";
import EditSkill from "@/pages/EditSkill";
import Earnings from "@/pages/Earnings";
import Admin from "@/pages/Admin";
import Wallet from "@/pages/Wallet";
import WalletSuccess from "@/pages/WalletSuccess";
import Profile from "@/pages/Profile";
import ResetPassword from "@/pages/ResetPassword";
import Legal from "@/pages/Legal";
import CreateOrg from "@/pages/CreateOrg";
import OrgJoin from "@/pages/OrgJoin";
import OrgCatalog from "@/pages/OrgCatalog";
import OrgMembers from "@/pages/OrgMembers";
import OrgAnalytics from "@/pages/OrgAnalytics";
import OrgSetup from "@/pages/OrgSetup";
import OrgQueue from "@/pages/OrgQueue";
import OrgArtifact from "@/pages/OrgArtifact";
import OrgAudit from "@/pages/OrgAudit";
import OrgBilling from "@/pages/OrgBilling";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Per-route document.title for browser tabs + SEO. Dynamic routes fall through
// to the prefix checks; SkillDetail overrides with the skill's own name on load.
const TITLES = {
  "/": "Runebox — Build & Sell Claude Skills and Agents",
  "/demo": "See Runebox in action",
  "/use-with-claude": "Use your skills in the Claude apps — Runebox",
  "/connect": "Add Runebox to Claude — Runebox",
  "/authorize": "Authorize — Runebox",
  "/login": "Sign in — Runebox",
  "/signup": "Create your account — Runebox",
  "/marketplace": "Marketplace — Claude Skills & Agents | Runebox",
  "/build": "Build a Claude Skill — Runebox",
  "/my-skills": "My Artifacts — Runebox",
  "/upload": "Upload a Skill — Runebox",
  "/earnings": "Earnings — Runebox",
  "/wallet": "Wallet — Runebox",
  "/admin": "Owner Console — Runebox",
  "/terms": "Terms of Service — Runebox",
  "/privacy": "Privacy Policy — Runebox",
  "/orgs/new": "Start a team registry — Runebox",
  "/orgs/join": "Join your team — Runebox",
};

// Aptabase (privacy-first, cookieless) fills the one funnel stage app data can't: gallery
// page views (GTM §5). The app key is a public client identifier — it ships in the bundle
// either way. Localhost is skipped so dev sessions don't pollute the numbers.
const TRACK = window.location.hostname !== "localhost";
if (TRACK) initAptabase("A-US-9990739803");

// IDs and slugs collapse to route patterns so the dashboard groups by page, not by UUID.
function routePattern(pathname) {
  return pathname
    .replace(/^\/skill\/[^/]+/, "/skill/:id")
    .replace(/^\/build\/[^/]+/, "/build/:session")
    .replace(/^\/orgs\/(?!new$|join$)[^/]+/, "/orgs/:slug")
    .replace(/\/artifacts\/[^/]+/, "/artifacts/:id");
}

function useRouteTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title =
      TITLES[pathname] ??
      (pathname.startsWith("/skill/") ? "Claude Skill — Runebox"
        : pathname.startsWith("/build/") ? "Build a Claude Skill — Runebox"
        : pathname.startsWith("/orgs/") ? "Org — Runebox"
        : "Runebox");
    if (TRACK) trackEvent("page_view", { path: routePattern(pathname) });
  }, [pathname]);
}

function AppRoutes() {
  useRouteTitle();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Login />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/build" element={<ProtectedRoute><SkillBuilder /></ProtectedRoute>} />
      <Route path="/build/:sessionId" element={<ProtectedRoute><SkillBuilder /></ProtectedRoute>} />
      <Route path="/my-skills" element={<ProtectedRoute><MySkills /></ProtectedRoute>} />
      <Route path="/upload" element={<ProtectedRoute><Upload /></ProtectedRoute>} />
      {/* Public for SEO — each skill is a rankable page. Buy/download still auth-gate. */}
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/skill/:skillId" element={<SkillDetail />} />
      <Route path="/skill/:skillId/edit" element={<ProtectedRoute><EditSkill /></ProtectedRoute>} />
      <Route path="/earnings" element={<ProtectedRoute><Earnings /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/wallet/success" element={<ProtectedRoute><WalletSuccess /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/terms" element={<Legal doc="terms" />} />
      <Route path="/privacy" element={<Legal doc="privacy" />} />
      <Route path="/orgs/new" element={<ProtectedRoute><CreateOrg /></ProtectedRoute>} />
      {/* Not protected — an invitee may not have an account yet; the page itself prompts login. */}
      <Route path="/orgs/join" element={<OrgJoin />} />
      <Route path="/orgs/:slug" element={<ProtectedRoute><OrgCatalog /></ProtectedRoute>} />
      <Route path="/orgs/:slug/members" element={<ProtectedRoute><OrgMembers /></ProtectedRoute>} />
      <Route path="/orgs/:slug/analytics" element={<ProtectedRoute><OrgAnalytics /></ProtectedRoute>} />
      <Route path="/orgs/:slug/queue" element={<ProtectedRoute><OrgQueue /></ProtectedRoute>} />
      <Route path="/orgs/:slug/audit" element={<ProtectedRoute><OrgAudit /></ProtectedRoute>} />
      <Route path="/orgs/:slug/billing" element={<ProtectedRoute><OrgBilling /></ProtectedRoute>} />
      <Route path="/orgs/:slug/artifacts/:id" element={<ProtectedRoute><OrgArtifact /></ProtectedRoute>} />
      <Route path="/orgs/:slug/setup" element={<ProtectedRoute><OrgSetup /></ProtectedRoute>} />
      <Route path="/" element={<Landing />} />
      <Route path="/demo" element={<SeeItInAction />} />
      <Route path="/connect" element={<ConnectClaude />} />
      <Route path="/authorize" element={<Authorize />} />
      {/* /use-with-claude retained from Phase 11 (manual ZIP upload) as a fallback path. */}
      <Route path="/use-with-claude" element={<UseWithClaude />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" theme="dark" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
